import type { OptionRight } from '@fleece/models';
import { CircleHelp, RefreshCw, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { BacktestResults } from '@/components/backtest-results';
import { ChainHelpDialog } from '@/components/chain-help-dialog';
import { ChartSettingsMenu } from '@/components/chart-settings-menu';
import { ChainTable, ChainTableSkeleton } from '@/components/chain-table';
import { PriceChart, PriceChartMessage, PriceChartSkeleton } from '@/components/price-chart';
import { SpreadPanel } from '@/components/spread-panel';
import { Button, Card, Input, Message, Segmented, Skeleton } from '@/components/ui';
import { useBacktest, useChain, useDailyBars, useEarnings, useStartBacktest } from '@/hooks/use-credit-spreads';
import { isBacktestLost } from '@/lib/backtest-status';
import { useElementWidth } from '@/hooks/use-element-width';
import { usePreferences } from '@/hooks/use-preferences';
import * as fmt from '@/lib/format';
import { defaultBacktestForm, defaultExpiration, toBacktestRequest, togglePick, toSpread } from '@/lib/spread';
import { cn } from '@/lib/utils';

const RANGES = [
  { value: '3M', label: '3M', days: 91 },
  { value: '6M', label: '6M', days: 182 },
  { value: '1Y', label: '1Y', days: 365 },
  { value: '2Y', label: '2Y', days: 730 },
] as const;

type Range = (typeof RANGES)[number]['value'];

function shiftDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The ticker and side live in the URL, so a reload or a shared link opens the same chain. */
function readUrl(): { symbol: string; right: OptionRight } {
  const params = new URLSearchParams(window.location.search);
  return { symbol: (params.get('symbol') ?? '').toUpperCase(), right: params.get('right') === 'call' ? 'call' : 'put' };
}

/**
 * Starts from the current query rather than an empty one, so a parameter this page does
 * not own — `?backend=`, which makes a bookmark or a shared link choose its service —
 * survives the first change of symbol.
 */
function writeUrl(symbol: string, right: OptionRight): void {
  const params = new URLSearchParams(window.location.search);
  params.delete('symbol');
  if (symbol.length > 0) {
    params.set('symbol', symbol);
  }
  params.set('right', right);
  window.history.replaceState(null, '', `?${params.toString()}`);
}

export function CreditSpreadsPage() {
  const { date, preferences } = usePreferences();
  const [initial] = useState(readUrl);
  const [draft, setDraft] = useState(initial.symbol);
  const [symbol, setSymbol] = useState(initial.symbol);
  const [right, setRight] = useState<OptionRight>(initial.right);
  // Choices are kept as keys, not rows, and resolved against whatever chain is loaded: a
  // refresh then keeps the pick and shows it at the new quotes, and an expiration the new
  // chain no longer has falls back to the default rather than to an empty table.
  const [chosenExpiration, setChosenExpiration] = useState<string | undefined>(undefined);
  const [pickedSymbols, setPickedSymbols] = useState<ReadonlyArray<string>>([]);
  const [range, setRange] = useState<Range>('1Y');
  const [backtestId, setBacktestId] = useState<string | undefined>(undefined);
  const [chainHelpOpen, setChainHelpOpen] = useState(false);
  const [today] = useState(() => fmt.easternDate(Date.now()));
  const chain = useChain(symbol, right);
  const earnings = useEarnings(symbol);
  const backtest = useBacktest(backtestId);
  const startBacktest = useStartBacktest();

  const rangeFrom = shiftDays(today, -(RANGES.find((r) => r.value === range)?.days ?? 365));
  const backtestFrom = backtest.data?.request.from;
  const chartFrom = backtestFrom !== undefined && backtestFrom < rangeFrom ? shiftDays(backtestFrom, -7) : rangeFrom;
  const bars = useDailyBars(symbol, chartFrom, today);

  useEffect(() => writeUrl(symbol, right), [symbol, right]);

  const expiration =
    chain.data === undefined
      ? undefined
      : chosenExpiration !== undefined && chain.data.expirations.includes(chosenExpiration)
        ? chosenExpiration
        : defaultExpiration(chain.data.rows);
  const rows = useMemo(() => (chain.data?.rows ?? []).filter((row) => row.expiration === expiration), [chain.data, expiration]);
  const picked = useMemo(() => {
    const bySymbol = new Map((chain.data?.rows ?? []).map((row) => [row.symbol, row]));
    return pickedSymbols.flatMap((s) => {
      const row = bySymbol.get(s);
      return row === undefined ? [] : [row];
    });
  }, [chain.data, pickedSymbols]);
  const spread = toSpread(picked);
  const positions = backtest.data?.result?.positions ?? [];
  // Gone from the service: its last-known `running` is stale and must not hold the page.
  const backtestLost = isBacktestLost(backtest.error);
  const [layoutRef, layoutWidth] = useElementWidth<HTMLDivElement>();
  const chartHeight = layoutWidth > 0 && layoutWidth < 640 ? 220 : 300;

  const submit = () => {
    const next = draft.trim().toUpperCase();
    if (next !== symbol) {
      setBacktestId(undefined);
      setPickedSymbols([]);
      setChosenExpiration(undefined);
    }
    setSymbol(next);
  };

  return (
    <div ref={layoutRef} className="mx-auto flex w-full max-w-[1400px] flex-col gap-2 p-2 sm:p-3">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="relative w-32">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted" />
          <Input
            aria-label="Ticker"
            placeholder="Ticker"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="pl-7 font-semibold uppercase"
          />
        </div>
        <Button type="submit" variant="primary">
          Load
        </Button>
        {chain.isPending && symbol.length > 0 && (
          <span role="status" aria-label="Loading the price">
            <Skeleton className="h-3 w-56" />
          </span>
        )}
        {chain.data !== undefined && (
          <span className="text-xs text-ink-2 tabular">
            <span className="font-semibold text-ink">{chain.data.underlying.symbol}</span> {fmt.price(chain.data.underlying.price)}
            <span className="ml-1 text-muted">
              last trade {date(fmt.easternDate(chain.data.underlying.pricedAt))} {fmt.easternTime(chain.data.underlying.pricedAt)} ET
            </span>
          </span>
        )}
        {symbol.length > 0 && (
          <Button variant="ghost" className="ml-auto" onClick={() => void chain.refetch()} disabled={chain.isFetching} aria-label="Refresh the chain">
            <RefreshCw className={cn('size-3.5', chain.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        )}
      </form>

      {symbol.length === 0 ? (
        <Card>
          <Message>Enter a ticker to load its daily chart and its out-of-the-money options 7 to 45 days out.</Message>
        </Card>
      ) : (
        <>
          <Card
            title="Price"
            actions={
              <span className="flex items-center gap-1">
                <Segmented<Range> label="Chart range" value={range} onChange={setRange} options={RANGES.map(({ value, label }) => ({ value, label }))} />
                <ChartSettingsMenu />
              </span>
            }
          >
            {bars.isPending ? (
              <PriceChartSkeleton height={chartHeight} />
            ) : bars.isError ? (
              <PriceChartMessage height={chartHeight}>{bars.error.message}</PriceChartMessage>
            ) : (
              <PriceChart
                symbol={symbol}
                bars={bars.data.bars}
                positions={positions}
                earnings={earnings.data?.reports ?? []}
                height={chartHeight}
                chartStyle={preferences.chartStyle}
              />
            )}
          </Card>

          {/* The side and the expiration choose what the chain shows, so they sit on it rather than above the chart. */}
          <div className="flex items-center gap-2">
            <Segmented<OptionRight>
              label="Option type"
              value={right}
              onChange={(next) => {
                setRight(next);
                setPickedSymbols([]);
              }}
              options={[
                { value: 'put', label: 'PUT' },
                { value: 'call', label: 'CALL' },
              ]}
            />
            {chain.isPending && (
              <div role="status" aria-label="Loading expirations" className="flex min-w-0 flex-1 gap-1 overflow-hidden pb-0.5">
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} className="h-7 w-[84px] shrink-0 rounded-md" />
                ))}
              </div>
            )}
            {chain.data !== undefined && chain.data.expirations.length > 0 && (
              <div role="tablist" aria-label="Expiration" className="scrollbar-thin flex min-w-0 flex-1 gap-1 overflow-x-auto pb-0.5">
                {chain.data.expirations.map((exp) => {
                  const dte = chain.data.rows.find((row) => row.expiration === exp)?.dte;
                  const selected = exp === expiration;
                  return (
                    <button
                      key={exp}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setChosenExpiration(exp)}
                      className={cn(
                        'flex h-7 shrink-0 items-center gap-1 rounded-md border px-2 text-xs whitespace-nowrap tabular',
                        selected ? 'border-accent bg-accent-soft text-ink' : 'border-line bg-surface text-ink-2 hover:text-ink',
                      )}
                    >
                      <span className="font-medium">{date(exp)}</span>
                      <span className="text-muted">{dte}d</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid items-start gap-2 lg:grid-cols-[minmax(0,1fr)_340px]">
            <Card
              title={expiration === undefined ? 'Chain' : `Chain · ${date(expiration)}`}
              actions={
                <span className="flex items-center gap-1.5">
                  <span className="hidden text-[11px] text-muted sm:inline">tap two strikes to build a spread</span>
                  <Button
                    variant="ghost"
                    className="size-6 px-0"
                    onClick={() => setChainHelpOpen(true)}
                    aria-label="What the columns mean"
                    aria-haspopup="dialog"
                    title="What the columns mean"
                  >
                    <CircleHelp className="size-3.5" />
                  </Button>
                </span>
              }
            >
              {chain.isPending ? (
                <ChainTableSkeleton right={right} />
              ) : chain.isError ? (
                <Message tone="error">{chain.error.message}</Message>
              ) : rows.length === 0 ? (
                <Message>No out-of-the-money {right}s 7 to 45 days out.</Message>
              ) : (
                <ChainTable right={right} rows={rows} picked={picked} spread={spread} onPick={(row) => setPickedSymbols(togglePick(picked, row).map((p) => p.symbol))} />
              )}
            </Card>

            <Card title="Spread" className="lg:sticky lg:top-2">
              {spread === undefined ? (
                <Message>{picked.length === 1 ? 'Pick a second strike in the same expiration.' : 'Pick two strikes from the chain. The one nearer the money is sold.'}</Message>
              ) : (
                <SpreadPanel
                  key={`${spread.short.symbol}|${spread.long.symbol}`}
                  right={right}
                  spread={spread}
                  initialForm={defaultBacktestForm(spread, today)}
                  earnings={earnings.data}
                  earningsError={earnings.error?.message}
                  running={startBacktest.isPending || (backtest.data?.status === 'running' && !backtestLost)}
                  error={startBacktest.error?.message}
                  onRun={(form) => startBacktest.mutate(toBacktestRequest(spread, form), { onSuccess: ({ backtestId: id }) => setBacktestId(id) })}
                />
              )}
            </Card>
          </div>

          {backtest.data !== undefined && !backtestLost && <BacktestResults backtest={backtest.data} />}
          {backtest.isError && (
            <Card>
              <Message tone="error">{backtest.error.message}</Message>
            </Card>
          )}
        </>
      )}
      <ChainHelpDialog open={chainHelpOpen} onClose={() => setChainHelpOpen(false)} />
    </div>
  );
}
